import type { MyProfileValues } from '@/lib/models/my-profile-schema';
import { refusalFromError } from '@/lib/permissions/access-denial';
import { apiErrorStatus, getApiErrorMessage } from './api-error';

/**
 * The signed-in user's own settings and basic details (Preferences page).
 *
 * Ported from glp-frontend (`apps/glp-frontend/src/`):
 * - `pages/settings/MySettings.tsx` — personal settings, `PATCH /api/users/{me}/`
 *   with `{language, timezone, date_format, time_format, currency}`; a blank
 *   field is sent as `null`, meaning "use the company default".
 * - `context/DateTimePreferenceProvider.tsx` — `inherited` and the per-field
 *   user → company → default resolution.
 * - `context/CurrencyContext.tsx` — the company display currency.
 * - `utils/datetime/{types,normalize}.ts` — formats and defaults.
 * - `dashboard/dashboard-admin/Users/UserProfile.jsx` — name/phone fields.
 */

export type DateFormatPref =
  | 'MM/DD/YYYY'
  | 'DD/MM/YYYY'
  | 'YYYY/MM/DD'
  | 'YYYY/DD/MM';
export type TimeFormatPref = '12h' | '24h';

export interface DateTimeSettings {
  timezone: string;
  dateFormat: DateFormatPref;
  timeFormat: TimeFormatPref;
}

export const DEFAULT_DATETIME_SETTINGS: DateTimeSettings = {
  timezone: 'UTC',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h'
};

export const DATE_FORMAT_OPTIONS: DateFormatPref[] = [
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
  'YYYY/DD/MM'
];

export const TIME_FORMAT_OPTIONS: Array<{
  value: TimeFormatPref;
  label: string;
}> = [
  { value: '12h', label: '12-hour (1:30 PM)' },
  { value: '24h', label: '24-hour (13:30)' }
];

/** glp `lib/constants.ts` `PARTNER_LANGUAGES` (what MySettings offers). */
export const LANGUAGE_OPTIONS = [
  'English',
  'Spanish',
  'Mandarin Chinese',
  'Hindi',
  'Arabic',
  'Portuguese',
  'Bengali',
  'Russian',
  'Japanese',
  'Punjabi',
  'German',
  'French',
  'Italian',
  'Korean',
  'Turkish',
  'Dutch',
  'Polish',
  'Swedish',
  'Danish',
  'Norwegian',
  'Swahili'
];

/** The slice of `GET /api/users/{id}` this page reads. */
export interface MyUserRecord {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  /** glp's admin user list (`GET /api/users/`) shows this as an `<img src>`. */
  profile_picture?: string | null;
  /**
   * glp's URL field for an uploaded photo (representatives store the full S3
   * URL here). Read first when the user record carries it.
   */
  profile_picture_url?: string | null;
  language?: string | null;
  timezone?: string | null;
  date_format?: string | null;
  time_format?: string | null;
  currency?: string | null;
  [key: string]: unknown;
}

/** The slice of `GET /api/company/preferences` this page reads. */
export interface CompanyPreferences {
  universal_defaults_pref?: {
    timezone?: string | null;
    time_format?: string | null;
    currency?: string | null;
    base_currency?: string | null;
    [key: string]: unknown;
  } | null;
  date_format_pref?: { date_format?: string | null } | null;
  time_format_pref?: { time_format?: string | null } | null;
  [key: string]: unknown;
}

/**
 * glp's `?id=` endpoint answers a list; the detail endpoint an object.
 *
 * With `userId`, only that user's row is taken: a backend that ignored the
 * `?id=` filter (answering the company's whole user list) must never put
 * another user's record on this page.
 */
export function pickUserRecord(data: unknown, userId?: string): MyUserRecord {
  const isRecord = (value: unknown): value is MyUserRecord =>
    !!value && typeof value === 'object' && !Array.isArray(value);
  if (userId) {
    const rows: unknown[] = Array.isArray(data) ? data : [data];
    const own = rows.find((row) => isRecord(row) && row.id === userId);
    return isRecord(own) ? own : {};
  }
  const record = Array.isArray(data) ? data[0] : data;
  return isRecord(record) ? record : {};
}

const usableUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  // glp's ImagePreviewCarousel defends against `.../undefined` URLs saved by
  // an old upload helper; never render one (or a stringified null).
  if (!trimmed || /(^|\/)(undefined|null)$/.test(trimmed)) return undefined;
  return trimmed;
};

/**
 * The user's photo, read as glp reads uploaded images: the `*_url` field
 * first (`AgentsAndReps.tsx` `profile_picture_url`, customers' `logo_url`),
 * then the bare field (`user-list-columns.tsx` `user.profile_picture`).
 */
/** Any record with glp's photo fields (user or representative). */
export interface PhotoFields {
  profile_picture_url?: unknown;
  profile_picture?: unknown;
}

export function profilePhotoUrl(
  user: PhotoFields | null | undefined
): string | undefined {
  return (
    usableUrl(user?.profile_picture_url) ?? usableUrl(user?.profile_picture)
  );
}

/** `null` / `''` / `'default'` means "inherit from the level below". */
export function inherited(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'default') return undefined;
  return trimmed;
}

export function isValidTimezone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || !zone.trim()) return false;
  try {
    // Throws a RangeError for an unknown zone.
    Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

function normalizeDateFormat(value: string): DateFormatPref {
  const upper = value.trim().toUpperCase().replace(/-/g, '/');
  return (DATE_FORMAT_OPTIONS as string[]).includes(upper)
    ? (upper as DateFormatPref)
    : DEFAULT_DATETIME_SETTINGS.dateFormat;
}

function normalizeTimeFormat(value: string): TimeFormatPref {
  if (value === '24h' || value === '24') return '24h';
  if (value === '12h' || value === '12') return '12h';
  return DEFAULT_DATETIME_SETTINGS.timeFormat;
}

export function resolveDateTimeSettings(
  user?: MyUserRecord | null,
  company?: CompanyPreferences | null
): DateTimeSettings {
  const timezone =
    inherited(user?.timezone) ??
    inherited(company?.universal_defaults_pref?.timezone);
  const dateFormat =
    inherited(user?.date_format) ??
    inherited(company?.date_format_pref?.date_format);
  const timeFormat =
    inherited(user?.time_format) ??
    inherited(company?.time_format_pref?.time_format) ??
    inherited(company?.universal_defaults_pref?.time_format);

  return {
    timezone:
      timezone && isValidTimezone(timezone)
        ? timezone
        : DEFAULT_DATETIME_SETTINGS.timezone,
    dateFormat: dateFormat
      ? normalizeDateFormat(dateFormat)
      : DEFAULT_DATETIME_SETTINGS.dateFormat,
    timeFormat: timeFormat
      ? normalizeTimeFormat(timeFormat)
      : DEFAULT_DATETIME_SETTINGS.timeFormat
  };
}

export function companyDisplayCurrency(
  company?: CompanyPreferences | null
): string {
  return (
    inherited(company?.universal_defaults_pref?.currency) ?? 'USD'
  ).toUpperCase();
}

export function companyBaseCurrency(
  company?: CompanyPreferences | null
): string {
  return (
    inherited(company?.universal_defaults_pref?.base_currency) ?? 'USD'
  ).toUpperCase();
}

export interface PersonalSettingsForm {
  language: string;
  timezone: string;
  date_format: string;
  time_format: string;
  currency: string;
}

const text = (value: unknown) => (typeof value === 'string' ? value : '');

export function toPersonalSettingsForm(
  user: MyUserRecord
): PersonalSettingsForm {
  return {
    language: text(user.language),
    timezone: text(user.timezone),
    date_format: text(user.date_format),
    time_format: text(user.time_format),
    currency: text(user.currency)
  };
}

export type PersonalSettingsPayload = {
  [K in keyof PersonalSettingsForm]: string | null;
};

export function buildPersonalSettingsPayload(
  form: PersonalSettingsForm
): PersonalSettingsPayload {
  return {
    language: form.language || null,
    timezone: form.timezone || null,
    date_format: form.date_format || null,
    time_format: form.time_format || null,
    currency: form.currency || null
  };
}

export function toProfileForm(user: MyUserRecord): MyProfileValues {
  return {
    first_name: text(user.first_name),
    last_name: text(user.last_name),
    phone: text(user.phone)
  };
}

/** The editable fields of My profile, as the user record stores them. */
export interface ProfilePatch {
  first_name?: string;
  last_name?: string | null;
  phone?: string | null;
}

/** A trimmed form value; an empty optional field is stored as `null`. */
const optional = (value: string): string | null => value.trim() || null;
/** A stored value as the record holds it (`null` for "none"). */
const stored = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

/**
 * The name/phone `PATCH users/{me}/` body: only the fields that differ from
 * the record as read, trimmed, with a cleared last name or phone sent as
 * `null` (how the record stores "none"; a blank string is a different value
 * and the serializer may refuse it). Never a photo, email, role or anything
 * else. Empty when nothing changed.
 */
export function buildProfilePatch(
  current: MyUserRecord,
  values: MyProfileValues
): ProfilePatch {
  const patch: ProfilePatch = {};
  const firstName = values.first_name.trim();
  if (firstName !== text(current.first_name).trim()) {
    patch.first_name = firstName;
  }
  const lastName = optional(values.last_name);
  if (lastName !== optional(text(current.last_name))) {
    patch.last_name = lastName;
  }
  const phone = optional(values.phone);
  if (phone !== optional(text(current.phone))) patch.phone = phone;
  return patch;
}

/** glp `UserProfile.jsx`'s `PUT /api/users/{me}/` body, plus the photo. */
export interface ProfilePhotoPutBody {
  phone: string | null;
  country: string | null;
  state: string | null;
  zip_code: string | null;
  department_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  address: string | null;
  secondary_address: string | null;
  language: string | null;
  timezone: string | null;
  date_format: string | null;
  time_format: string | null;
  company_id?: string;
  additional_fields: unknown[];
  profile_picture_url: string;
  profile_picture: string;
}

/**
 * The PUT sent when a new photo is saved (the user confirmed on 2026-10-08
 * that only a PUT replacing `profile_picture_url` showed a photo). It is
 * glp's "Your own details" PUT body (`dashboard-admin/Users/UserProfile.jsx`
 * `handleSubmit`: its form fields from the `?id=` row,
 * `company_id: record.company.id`, `additional_fields`) built from the record
 * as just read, with the edited name/phone and both photo fields set to the
 * new S3 URL. Nothing else: no id, role, permissions, status or company
 * record.
 *
 * Unlike glp's admin form, a field the record stores as `null` is resent as
 * `null`, not `''`: a rep's record has no address, country, state, zip or
 * language, and the serializer is not asked to accept blanks it may refuse.
 *
 * Note: the user serializer has no photo field (`GET`/`OPTIONS
 * /api/users/{me}/`, 2026-10-13), so check what the server kept with
 * `isPhotoStored` afterwards.
 */
export function buildProfilePhotoPutBody(
  current: MyUserRecord,
  values: MyProfileValues,
  photoUrl: string
): ProfilePhotoPutBody {
  if (!photoUrl) throw new Error('No uploaded photo URL');
  const company = current.company as
    | { id?: unknown }
    | string
    | null
    | undefined;
  let companyId: string | undefined;
  if (typeof company === 'string' && company) companyId = company;
  else if (typeof company === 'object' && typeof company?.id === 'string') {
    companyId = company.id;
  }
  const body: ProfilePhotoPutBody = {
    phone: optional(values.phone),
    country: stored(current.country),
    state: stored(current.state),
    zip_code: stored(current.zip_code),
    // glp reads `department_id` from the row (the serializer has none) and
    // so sends '' unless an admin picks a department; kept as glp sends it.
    department_id: text(current.department_id),
    first_name: values.first_name.trim(),
    last_name: optional(values.last_name),
    email: text(current.email),
    address: stored(current.address),
    secondary_address: stored(current.secondary_address),
    language: stored(current.language),
    timezone: text(current.timezone) || null,
    date_format: text(current.date_format) || null,
    time_format: text(current.time_format) || null,
    additional_fields: Array.isArray(current.additional_fields)
      ? current.additional_fields
      : [],
    profile_picture_url: photoUrl,
    profile_picture: photoUrl
  };
  if (companyId) body.company_id = companyId;
  return body;
}

/**
 * The cached record right after a successful profile save: what was sent,
 * overlaid by whatever the PATCH answered (the server's own non-empty values
 * win). The page refetches straight after, so a reload shows the server's
 * record either way.
 */
export function mergeSavedProfile(
  previous: MyUserRecord | undefined,
  payload: ProfilePatch | ProfilePhotoPutBody,
  response: MyUserRecord | undefined
): MyUserRecord {
  const merged: MyUserRecord = { ...(previous ?? {}), ...payload };
  Object.entries(response ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      merged[key] = value;
    }
  });
  return merged;
}

/**
 * Whether the server kept a newly saved photo: some record the portal reads
 * the photo from (the PUT's answer, the user record read again, the
 * representative record) now carries exactly that URL. The user serializer
 * has no photo field (2026-10-13), so a 2xx alone does not mean the photo
 * was saved.
 */
export function isPhotoStored(
  photoUrl: string,
  records: ReadonlyArray<object | null | undefined>
): boolean {
  if (!photoUrl) return false;
  return records.some((record) => {
    const photo = record as PhotoFields | null | undefined;
    return [photo?.profile_picture_url, photo?.profile_picture].some(
      (value) => usableUrl(value) === photoUrl
    );
  });
}

/** DRF's stock 403 sentence; it says nothing the rep can act on. */
const DRF_PERMISSION_DENIED =
  'You do not have permission to perform this action.';

/**
 * The message for a failed self-service save (My profile, personal
 * settings).
 *
 * - A portal refusal (403 `PORTAL_PERMISSION_REQUIRED`) keeps the backend's
 *   own sentence; the axios interceptor also opens the access-request dialog.
 * - Any other 403 says the change was refused and who can make it, keeping
 *   the backend's reason when it gives one beyond DRF's stock sentence.
 * - 400 lists the field errors; 5xx, network failures and timeouts get
 *   `fallback`; a client-side failure (the photo upload) keeps its message
 *   (`getApiErrorMessage`).
 */
export function selfServiceSaveError(
  error: unknown,
  fallback: string,
  what = 'these details'
): string {
  if (apiErrorStatus(error) === 403 && !refusalFromError(error)) {
    const reason = getApiErrorMessage(error, '');
    const guidance = 'Your administrator can update them in Global Link+.';
    return reason && reason !== DRF_PERMISSION_DENIED
      ? `${reason} ${guidance}`
      : `You don't have permission to change ${what}. ${guidance}`;
  }
  return getApiErrorMessage(error, fallback);
}

/**
 * The S3 folder for the rep's photo: glp's agent create/edit forms upload a
 * representative's photo under `agents/agents-profile-pictures/{companyId}/…`
 * (`utils/upload-files` `uploadFile`, same presigned-URL flow and bucket as
 * `uploadfiles3`). Keyed here by the user id rather than glp's whitespace-
 * stripped name so two reps with the same name never share a folder.
 */
export function profilePhotoUploadPath(
  user: MyUserRecord,
  userId: string
): string {
  const company = user.company as { id?: unknown } | string | null | undefined;
  let companyId = '';
  if (typeof company === 'string') companyId = company;
  else if (typeof company?.id === 'string') companyId = company.id;
  return companyId
    ? `agents/agents-profile-pictures/${companyId}/${userId}`
    : `agents/agents-profile-pictures/${userId}`;
}

/** `lib/utils/uploadfiles3` caps the S3 PUT at 5 MB. */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

export function validateAvatarFile(file: {
  type: string;
  size: number;
}): string | null {
  if (!file.type.startsWith('image/')) return 'Choose an image file.';
  if (file.size > AVATAR_MAX_BYTES) return 'The photo must be 5 MB or smaller.';
  return null;
}

/**
 * The only user record this page writes: the signed-in user's, by the
 * `userId` session cookie. Never built from anything else.
 */
export function myUserPath(userId: string | undefined | null): string {
  if (!userId) throw new Error('No signed-in user');
  return `users/${encodeURIComponent(userId)}/`;
}

/**
 * The signed-in user's record as glp's self-service page reads it
 * (`pages/settings/MySettings.tsx`: `GET /api/users/?id={me}`). glp only ever
 * reads `profile_picture` from that list endpoint (the admin user list); its
 * detail type (`USER`) has no photo field.
 */
export function myUserReadPath(userId: string | undefined | null): string {
  if (!userId) throw new Error('No signed-in user');
  return `users/?id=${encodeURIComponent(userId)}`;
}

/**
 * The signed-in user's record, robust to either backend shape for the photo.
 *
 * Fields come from the own row of glp's `GET users/?id={me}` (MySettings).
 * The photo is the first usable of: that row's `profile_picture_url` /
 * `profile_picture`, then the detail `GET users/{me}` record's (read only
 * when the row has no usable photo). When the list read fails or has no own
 * row, the detail record is used for everything. A detail record whose id is
 * not the session's is never used.
 */
export async function loadMyUser(
  get: (path: string) => Promise<unknown>,
  userId: string
): Promise<MyUserRecord> {
  const detailPath = myUserPath(userId).replace(/\/$/, '');
  const readDetail = async (): Promise<MyUserRecord | undefined> => {
    const record = pickUserRecord(await get(detailPath));
    // The detail URL is addressed by the session's id; a record without an
    // id is taken as that user's, one with another id never is.
    return !record.id || record.id === userId ? record : undefined;
  };

  let own: MyUserRecord = {};
  try {
    own = pickUserRecord(await get(myUserReadPath(userId)), userId);
  } catch {
    // Fall through to the detail endpoint.
  }
  if (!own.id) return (await readDetail()) ?? {};
  if (profilePhotoUrl(own)) return own;

  try {
    const photo = profilePhotoUrl(await readDetail());
    return photo
      ? { ...own, profile_picture: photo, profile_picture_url: photo }
      : own;
  } catch {
    return own;
  }
}

/** "Right now that reads …" in the resolved zone and formats. */
export function formatPreview(at: Date, settings: DateTimeSettings): string {
  const twelve = settings.timeFormat === '12h';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: settings.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: twelve ? 'numeric' : '2-digit',
    minute: '2-digit',
    hourCycle: twelve ? 'h12' : 'h23'
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';

  const day = part('day');
  const month = part('month');
  const year = part('year');
  const date = {
    'MM/DD/YYYY': `${month}/${day}/${year}`,
    'DD/MM/YYYY': `${day}/${month}/${year}`,
    'YYYY/MM/DD': `${year}/${month}/${day}`,
    'YYYY/DD/MM': `${year}/${day}/${month}`
  }[settings.dateFormat];
  const time = `${part('hour')}:${part('minute')}`;
  return twelve ? `${date} ${time} ${part('dayPeriod')}` : `${date} ${time}`;
}
